<template>
    <FlexGrid
      :hideScopeUnselectedMessage="true"
      :ColumnsDefinition="getColumnsDefinition"
      :GridId="gridId"
      :displayLang="lang"
      :GridDataParams="gridData"
      :FiltersDefinition="getFiltersDefinition"
      :FilterColumns="getFilterColumns"
      :onUpdateData="onUpdateData"
      :onCountRows="countRows"
      :IgnoreGroupingColumns="getIgnoreGroupingColumns"
      :groupHeaderFormat="getGroupHeaderFormat"     
      :ExportsButtons="getExportsButtons"
      :WijmoLicense="conf.value('VUE_APP_WIJMO_LICENCE')"
    />
    <Popup :totalCount="totalCount" @initPopup="initPopup" />
</template>

<script>

import { computed } from "vue";

// Local components
import Popup from '@/components/atoms/Popup.vue';

import XlsExport from "@/Utils/xls-export.js";
import config from "../../../configs";

// Vue components
import FlexGrid from "@supervision/vue-components/components/FlexGrid.vue";
import { GridDataParams, PaginationDescription } from "@supervision/vue-components/types/GridDataParams.js";

export default {
    name: 'AdditionalDataGrid',
    inject: ["lang"],
    components: {
        Popup,
        FlexGrid
    },
    props: {
        rawData: {
            type: Object,
            required: true,
        },
        externalId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            conf: config,
            gridId: 'additionalDataGrid',
            totalCount: 0,
            popup: null,
            columnsDefinition: [
                {
                    binding: 'label',
                    filter: {
                        hasConditionFilter: false,
                        hasValueFilter: true,
                        values: computed(() => this.getValuesFilter?.label),
                    },
                },
                {
                    binding: 'value',
                    filter: {
                        hasConditionFilter: true,
                        hasValueFilter: false,
                        values: computed(() => this.getValuesFilter?.value),
                    },
                },
                {
                    binding: 'source',
                    filter: {
                        hasConditionFilter: false,
                        hasValueFilter: true,
                        values: computed(() => this.getValuesFilter?.source),
                    },
                }
            ],
            latestFilter: null,
            gridData: new GridDataParams(null, { PageSize: this.defaultPageSize }),
        };
    },
    methods: {
        initPopup(popup) {
            this.popup = popup;
        },
        countRows() {
            this.popup.show(true, () => {});
        },
        exportCsv() {
            var xls = new XlsExport(this.translateExportData);
            xls.exportToCSV(`${this.$t("pages.DeviceDetails.Title")}_${this.$t("pages.DeviceDetails.AdditionalDataGrid.ExportName")}(${
                this.externalId
            })`,
            );
        },
        exportExcel() {
            var xls = new XlsExport(this.translateExportData);
            xls.exportToXLS(`${this.$t("pages.DeviceDetails.Title")}_${this.$t("pages.DeviceDetails.AdditionalDataGrid.ExportName")}(${
                this.externalId
            })`,
            );
        },

        applyFiltersAndSort(queryDescription) {
            const { FilterDescription, SortDescription } = queryDescription;

            // Appliquer les filtres
            let filteredItems = this.getTransformedRawData.Data;

            FilterDescription.ValueFilters.forEach(filter => {
                if (filter.AffectedColumn && Object.keys(filter.ShowValues).length > 0) {
                filteredItems = filteredItems.filter(item => {
                    const value = item[filter.AffectedColumn];
                    return filter.ShowValues[value] === true;
                });
                }
            });

            FilterDescription.ConditionFilters.forEach(conditionFilter => {
                if (conditionFilter.AffectedColumn) {
                
                const validConditions = conditionFilter.Conditions.filter(condition => condition.value != null && condition.value !== "");

                if (validConditions.length > 0) {
                    filteredItems = filteredItems.filter(item => {
                    return validConditions.every(condition => {
                        const itemValue = item[conditionFilter.AffectedColumn] != null ? String(item[conditionFilter.AffectedColumn]) : "";
                    
                        const lowerItemValue = itemValue.toLowerCase();
                        const lowerConditionValue = condition.value != null ? String(condition.value).toLowerCase() : "";

                        switch (condition.operator) {
                        case -1: // Égal à (ignore la casse)
                            return lowerItemValue === lowerConditionValue;
                        case 1:  // Différent de (ignore la casse)
                            return lowerItemValue !== lowerConditionValue;
                        case 6:  // Commence par (ignore la casse)
                            return lowerItemValue.startsWith(lowerConditionValue);
                        case 7:  // Se termine par (ignore la casse)
                            return lowerItemValue.endsWith(lowerConditionValue);
                        case 8:  // Contient (ignore la casse)
                            return lowerItemValue.includes(lowerConditionValue);                                          
                        case 9:  // Ne contient pas (nouveau cas, ignore la casse)
                            return !lowerItemValue.includes(lowerConditionValue);
                        default:
                            return true;
                        }
                    });
                    });
                }
                }
            });

            // Appliquer le tri
            if (SortDescription && Object.keys(SortDescription).length > 0) {
                const sortColumn = SortDescription.AffectedColumn;
                const sortDirection = SortDescription.Direction || 'asc';

                filteredItems = filteredItems.sort((a, b) => {
                if (a[sortColumn] < b[sortColumn]) {
                    return sortDirection === 'asc' ? -1 : 1;
                }
                if (a[sortColumn] > b[sortColumn]) {
                    return sortDirection === 'asc' ? 1 : -1;
                }
                return 0;
                });
            }

            const paginationDescription = new PaginationDescription(
                50,
                1,
                filteredItems.length
            );

            return new GridDataParams(filteredItems, paginationDescription)
        },
    
        onUpdateData(
            queryDescription = null,
            mustReload = true,
            fetchAll = false,
            shouldUpdateReq = true) {
            console.log("onUpdateData", queryDescription, mustReload, fetchAll, shouldUpdateReq);
            this.gridData = this.applyFiltersAndSort(queryDescription);
        },
    },
    computed: {
        // IgnoreGroupingColumns
        getIgnoreGroupingColumns() {
        return this.columnsDefinition
            .filter((c) => c.ignoreGroupingColumns)
            .map(({ binding }) => binding);
        },
        getValuesFilter() {
            const mapData = (key) => {
                const uniqueValues = new Set();
                return this.getTransformedRawData.Data
                    .filter(item => {
                        const isUnique = !uniqueValues.has(item[key]);
                        uniqueValues.add(item[key]);
                        return isUnique;
                    })
                    .map(item => ({
                        id: item[key],
                        label: item[key],
                    }));
            };

            return {
                label: mapData('label'),
                value: mapData('value'),
                source: mapData('source'),
            };
        },

        translateExportData() {
            return this.getTransformedRawData.Data.map((row) => {
                let obj = {};
                obj[this.$t("pages.DeviceDetails.AdditionalDataGrid.columns.label")] = row.label;
                obj[this.$t("pages.DeviceDetails.AdditionalDataGrid.columns.value")] = row.value;
                obj[this.$t("pages.DeviceDetails.AdditionalDataGrid.columns.source")] = row.source;
                return obj;
            });
        },
        getTransformedRawData() {
            let jsonParams = null;
            let paramData = [];

            if (this.rawData) {
                    let additionalParams = this.rawData.filter(el => el.key === "AdditionalData"); //[0].value
                    if(additionalParams.length > 0 && additionalParams[0].value && additionalParams[0].value !== "") {
                        jsonParams = JSON.parse(additionalParams[0].value);
                }
            }

            if(jsonParams) {
                    paramData = Object.values(jsonParams).map((value, _idx) => ({
                        label: Object.keys(jsonParams)[_idx],
                        value: value,
                        source: "Asset",
                }));
            }

            const paginationDescription = new PaginationDescription(
                50,
                1,
                paramData.length
            );

            return new GridDataParams(paramData, paginationDescription)
        },
        getFilterColumns() {
            return this.columnsDefinition
                .filter((c) => Object.hasOwnProperty.call(c, "filter"))
                .map(({ binding }) => binding);
        },
        getColumnsDefinition() {
            return this.columnsDefinition.map((c) => {
                const obj = { ...c };
                const { binding, ...r } = obj;
                return {
                    binding,
                    header: this.$t(`pages.DeviceDetails.AdditionalDataGrid.columns.${binding}`),
                    width: "*",
                    ...r,
                };
            });
        },
        getFiltersDefinition() {
            return this.columnsDefinition
                .filter((c) => Object.hasOwnProperty.call(c, "filter"))
                .map((c) => {
                const { binding, filter } = c;
                return {
                    binding,
                    ...filter,
                };
            });
        },
        getExportsButtons() {
            return {
                Header: {
                    text: this.$t("common.exports"),
                    icon: "scf-export",
                },
                DataSource: [
                    {
                        text: this.$t("common.exportCsv"),
                        method: "exportCsv",
                        icon: "scf-csv",
                    },
                    {
                        text: this.$t("common.exportExcel"),
                        method: "exportExcel",
                        icon: "scf-excel",
                    },
                ],
                Events: {
                exportCsv: this.exportCsv,
                exportExcel: this.exportExcel,
                },
            };
        },
        getGroupHeaderFormat(item, propName) {
            return item[propName];
        },
    },

    mounted() {
        this.gridData = this.getTransformedRawData;
        this.totalCount = this.getTransformedRawData.Data.length;
    },
};
</script>